import { Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { TenantRead, UserRead } from "@nens/rana/api-v1";
import { RanaLoading, RanaModal } from "@nens/ranalib";

import { cancelButtonText } from "@/translations";

import { useUpdateUsersAcrossTenants } from "../api/update-user-role-across-tenants";
import { USER_SEARCH_KEY } from "../hooks/use-admin-tenant-user-list";

interface AdminTenantUserDeleteModalProps {
  tenants: TenantRead[];
  user: UserRead;
  onClose: () => void;
}

export function AdminTenantUserDeleteModal({
  tenants,
  user,
  onClose,
}: AdminTenantUserDeleteModalProps) {
  const intl = useIntl();
  const { mutateAsync, isMutating, isError } = useUpdateUsersAcrossTenants(user.id);
  const navigate = useNavigate();

  const deleteUser = async () => {
    await mutateAsync(tenants.map(({ id }) => ({ type: "delete-from-org", organisationId: id })));
    const params = new URLSearchParams(window.location.search);
    params.delete(USER_SEARCH_KEY);
    navigate({ search: params.toString() });
    onClose();
  };

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <FormattedMessage
          id="header.delete_user"
          defaultMessage="Do you want to delete this user"
        />
      }
      maxW="xl"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: intl.formatMessage({
            id: "button.delete_user",
            defaultMessage: "Delete user",
          }),
          variant: "danger",
          isDisabled: isMutating,
          onClick: deleteUser,
        },
      ]}
    >
      <Flex flexDir="column" gap={2}>
        <Text>
          <FormattedMessage
            id="paragraph.delete_user"
            defaultMessage="Deleted user accounts no longer have access to their data, projects and the Rana platform itself."
          />
        </Text>

        {isMutating && <RanaLoading size="md" isCentered />}
        {isError && (
          <Text color="red.500">
            <FormattedMessage
              id="error.delete_user_failed"
              defaultMessage="Failed to delete user account"
            />
          </Text>
        )}
      </Flex>
    </RanaModal>
  );
}
